import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TextSnippet as TemplateIcon,
} from '@mui/icons-material';
import { AdminLayout } from '../components/AdminLayout';
import apiClient from '../utils/api';
import { MessageTemplate } from '../types';

const CATEGORY_LABELS: Record<string, string> = {
  general: '一般',
  support: 'サポート',
  technical: '技術',
  other: 'その他',
};

const CATEGORY_COLORS: Record<string, 'primary' | 'info' | 'warning' | 'default'> = {
  general: 'primary',
  support: 'info',
  technical: 'warning',
  other: 'default',
};

interface TemplateForm {
  title: string;
  content: string;
  category: string;
}

const TemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<MessageTemplate | null>(null);
  const [form, setForm] = useState<TemplateForm>({ title: '', content: '', category: 'general' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getTemplates();
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (err) {
      console.error('テンプレート取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreateOpen = () => {
    setEditingTemplate(null);
    setForm({ title: '', content: '', category: 'general' });
    setDialogOpen(true);
  };

  const handleEditOpen = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setForm({ title: template.title, content: template.content, category: template.category });
    setDialogOpen(true);
  };

  const handleDeleteOpen = (template: MessageTemplate) => {
    setDeletingTemplate(template);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      if (editingTemplate) {
        await apiClient.updateTemplate(editingTemplate.id, form);
        setSnackbar({ open: true, message: 'テンプレートを更新しました', severity: 'success' });
      } else {
        await apiClient.createTemplate(form as any);
        setSnackbar({ open: true, message: 'テンプレートを作成しました', severity: 'success' });
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      console.error('テンプレート保存エラー:', err);
      setSnackbar({ open: true, message: '保存に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    try {
      await apiClient.deleteTemplate(deletingTemplate.id);
      setSnackbar({ open: true, message: 'テンプレートを削除しました', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (err) {
      console.error('テンプレート削除エラー:', err);
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon sx={{ fontSize: 28, color: '#1976d2' }} />
            <Typography variant="h5" fontWeight="bold">
              テンプレート管理
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}>
            新規作成
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          メッセージテンプレートを管理します。ここで作成したテンプレートは、チャット画面やルーム作成時の初期メッセージで利用できます。
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <TemplateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              テンプレートがまだありません
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateOpen}>
              最初のテンプレートを作成
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>タイトル</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>内容</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>カテゴリ</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>使用回数</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 120 }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>
                      {template.title}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {template.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={CATEGORY_LABELS[template.category] || template.category}
                        color={CATEGORY_COLORS[template.category] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {template.usage_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="編集">
                        <IconButton size="small" onClick={() => handleEditOpen(template)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton size="small" color="error" onClick={() => handleDeleteOpen(template)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* 作成・編集ダイアログ */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTemplate ? 'テンプレート編集' : 'テンプレート新規作成'}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="タイトル"
              fullWidth
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              sx={{ mt: 1, mb: 2 }}
              placeholder="例：初回ご挨拶"
            />
            <TextField
              label="テンプレート内容"
              fullWidth
              multiline
              rows={8}
              value={form.content}
              onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="テンプレートの本文を入力してください"
            />
            <FormControl fullWidth size="small">
              <InputLabel>カテゴリ</InputLabel>
              <Select
                value={form.category}
                label="カテゴリ"
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="general">一般</MenuItem>
                <MenuItem value="support">サポート</MenuItem>
                <MenuItem value="technical">技術</MenuItem>
                <MenuItem value="other">その他</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !form.title.trim() || !form.content.trim()}
            >
              {saving ? <CircularProgress size={20} /> : editingTemplate ? '更新' : '作成'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>テンプレート削除</DialogTitle>
          <DialogContent>
            <Typography>
              「{deletingTemplate?.title}」を削除してもよろしいですか？
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              削除
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
        />
      </Box>
    </AdminLayout>
  );
};

export default TemplateManagement;
